import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, ChevronRight, AlertCircle, Loader2, MessageSquare, CheckCircle, HelpCircle, ArrowRight } from 'lucide-react';
import { getSubdomain } from '../utils/subdomainHelper';
import publicApiClient from '../services/publicApiClient';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Card from '../components/common/Card';
import Alert from '../components/common/Alert';
import Checkbox from '../components/common/Checkbox';
import IconButton from '../components/common/IconButton';

const CustomerLoginPage = () => {
    const navigate = useNavigate();
    const subdomain = getSubdomain();

    // Login Form State
    const [mobileNumber, setMobileNumber] = useState('+971');
    const [pin, setPin] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Subdomain & Tenant Info State
    const [tenantLoading, setTenantLoading] = useState(!!subdomain);
    const [tenantError, setTenantError] = useState(null);
    const [tenantInfo, setTenantInfo] = useState(null);

    // Forgot PIN State
    const [view, setView] = useState('login'); // 'login' | 'forgot-pin'
    const [forgotMobile, setForgotMobile] = useState('+971');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSuccess, setForgotSuccess] = useState(null);

    // Manual Redirection State (if no subdomain active)
    const [manualSlug, setManualSlug] = useState('');

    // Setup Check States
    const [hasPin, setHasPin] = useState(true);
    const [customerName, setCustomerName] = useState(null);
    const [mobileStatusLoading, setMobileStatusLoading] = useState(false);
    const [mobileError, setMobileError] = useState(null);

    const checkMobileSetup = async (number) => {
        if (!number || number.trim() === '+971' || number.trim().length < 9) {
            setHasPin(true);
            setCustomerName(null);
            setMobileError(null);
            return;
        }

        const cleanNumber = number.replace(/\s+/g, '');
        setMobileStatusLoading(true);
        try {
            const response = await publicApiClient.get(`/public/customers/check-setup?mobileNumber=${encodeURIComponent(cleanNumber)}&subdomain=${subdomain}`);
            setHasPin(response.data.hasPin);
            setCustomerName(response.data.customerName);
            setMobileError(null);
        } catch (err) {
            console.warn('Failed to check PIN setup status:', err);
            setCustomerName(null);
            if (err.response?.status === 404) {
                setMobileError("This mobile number is not registered for portal access. Please contact your supplier.");
                setHasPin(true);
            } else {
                setMobileError(null);
                setHasPin(true);
            }
        } finally {
            setMobileStatusLoading(false);
        }
    };

    // Watch mobileNumber change to auto check setup
    useEffect(() => {
        const clean = mobileNumber.replace(/\s+/g, '');
        if (clean.length >= 13) {
            checkMobileSetup(mobileNumber);
        } else {
            setHasPin(true);
            setCustomerName(null);
            setMobileError(null);
        }
    }, [mobileNumber, subdomain]);

    const handleMobileBlur = () => {
        const clean = mobileNumber.replace(/\s+/g, '');
        if (clean.length >= 9 && clean.length < 13) {
            checkMobileSetup(mobileNumber);
        }
    };

    // Fetch Tenant Branding Info on Mount
    useEffect(() => {
        if (!subdomain) return;

        const fetchBranding = async () => {
            try {
                setTenantLoading(true);
                setTenantError(null);
                const response = await publicApiClient.get(`/public/customers/tenant-info?subdomain=${subdomain}`);
                setTenantInfo(response.data);
            } catch (err) {
                console.error('Failed to fetch tenant branding:', err);
                setTenantError(err.response?.data?.error || 'Could not load supplier portal. Please verify the URL.');
            } finally {
                setTenantLoading(false);
            }
        };

        fetchBranding();
    }, [subdomain]);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const response = await publicApiClient.post('/public/customers/login', {
                mobileNumber,
                pin,
                subdomain
            });

            const data = response.data;

            if (data.token) {
                // Store Customer Session JWT
                if (rememberMe) {
                    localStorage.setItem('customer_token', data.token);
                    localStorage.setItem('customer_data', JSON.stringify(data));
                } else {
                    sessionStorage.setItem('customer_token', data.token);
                }
                
                // Redirect to shop portal
                navigate(`/shop?token=${data.token}`);
            } else {
                setError('Authentication failed. Please verify credentials.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError(err.response?.data?.error || 'Invalid credentials or connection issue.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestPin = async (e) => {
        if (e) e.preventDefault();
        setForgotSuccess(null);
        setError(null);
        setView('login');
        setForgotLoading(true);

        try {
            const response = await publicApiClient.post('/public/customers/forgot-pin', {
                mobileNumber: forgotMobile,
                subdomain
            });

            setForgotSuccess(response.data.message || 'Temporary PIN sent successfully.');
            setForgotMobile('');
            setTimeout(() => {
                setView('login');
                setForgotSuccess(null);
            }, 6000);
        } catch (err) {
            console.error('PIN Reset Error:', err);
            setError(err.response?.data?.error || 'Failed to request PIN. Verify your number.');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleManualRedirect = (e) => {
        if (e) e.preventDefault();
        if (!manualSlug.trim()) return;

        const slug = manualSlug.trim().toLowerCase();
        // Redirect to subdomain host
        const port = window.location.port ? `:${window.location.port}` : '';
        const baseDomain = window.location.hostname.replace('www.', '').replace('localhost', 'localhost');
        
        if (baseDomain === 'localhost') {
            window.location.href = `${window.location.protocol}//${slug}.localhost${port}`;
        } else {
            window.location.href = `${window.location.protocol}//${slug}.${baseDomain}${port}`;
        }
    };

    // --- RENDER 1: No Subdomain is Active ---
    if (!subdomain) {
        return (
            <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-950 flex flex-col items-center justify-center p-6 font-sans">
                <Card className="w-full max-w-md text-center border border-slate-100 dark:border-slate-800">
                    <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <HelpCircle className="w-8 h-8" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-slate-100 mb-2">Access Ordering Portal</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                        To place B2B orders, please access the portal using your supplier's custom link (e.g. <code>supplier-name.yaseero.com</code>).
                    </p>

                    <form onSubmit={handleManualRedirect} className="space-y-4">
                        <div className="text-left">
                            <Input
                                label="Enter Supplier Slug"
                                placeholder="e.g. gulf-foods"
                                value={manualSlug}
                                onChange={(e) => setManualSlug(e.target.value)}
                                required
                                rightElement={
                                    <IconButton
                                        type="submit"
                                        icon={ArrowRight}
                                        ariaLabel="Go to supplier page"
                                        variant="primary"
                                        size="sm"
                                        className="h-9 w-9 bg-slate-900 dark:bg-slate-700"
                                    />
                                }
                            />
                        </div>
                    </form>

                    <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
                        <p className="text-slate-400 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider">
                            Securely powered by <span className="text-slate-600 dark:text-slate-300 font-bold">Yaseero</span>
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // --- RENDER 2: Subdomain Loading ---
    if (tenantLoading) {
        return (
            <div className="min-h-[100dvh] bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-6">
                <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mb-4" />
                <p className="text-slate-500 dark:text-slate-400 font-bold text-xs uppercase tracking-widest">Loading Supplier Portal...</p>
            </div>
        );
    }

    // --- RENDER 3: Subdomain Resolution Error ---
    if (tenantError) {
        return (
            <div className="min-h-[100dvh] bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-6">
                <Card className="w-full max-w-md border border-red-100 dark:border-red-900/30 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-black text-slate-900 dark:text-slate-100 mb-2">Portal Unavailable</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">{tenantError}</p>
                    <Button 
                        onClick={() => window.location.reload()}
                        className="w-full bg-slate-900 dark:bg-slate-700 text-white font-bold"
                    >
                        Try Again
                    </Button>
                </Card>
            </div>
        );
    }

    // --- RENDER 4: Main Customer View (Login or Forgot PIN) ---
    return (
        <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-cyan-50/30 dark:from-slate-900 dark:to-slate-950 flex flex-col items-center justify-center p-6 font-sans">
            <Card className="w-full max-w-md p-0 overflow-hidden border border-slate-100/50 dark:border-slate-800 shadow-2xl shadow-slate-200/60 dark:shadow-none">
                
                {/* Supplier Custom Header Branding */}
                <div className="bg-gradient-to-br from-cyan-600 to-blue-700 p-8 text-white text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/5 rounded-full -ml-12 -mb-12 blur-xl"></div>
                    
                    <div className="relative z-10 flex flex-col items-center">
                        {tenantInfo?.logoUrl ? (
                            <img src={tenantInfo.logoUrl} alt={tenantInfo.companyName} className="h-16 w-16 rounded-2xl object-cover mb-4 shadow-md bg-white border border-white/20" />
                        ) : (
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 shadow-lg border border-white/20">
                                <span className="text-white text-2xl font-black">{tenantInfo?.companyName?.charAt(0) || 'S'}</span>
                            </div>
                        )}
                        <h1 className="text-2xl font-black tracking-tight uppercase">
                            {tenantInfo?.companyName || 'Ordering Portal'}
                        </h1>
                        <p className="text-cyan-100 text-xs font-semibold mt-1 tracking-wider uppercase opacity-90">
                            {view === 'login' ? 'Customer Sign In' : 'Request Password Reset'}
                        </p>
                    </div>
                </div>

                {/* --- LOGIN VIEW --- */}
                {view === 'login' && (
                    <form onSubmit={handleLogin} className="p-8 space-y-6">
                        {error && (
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        )}

                        {forgotSuccess && (
                            <Alert variant="success">
                                {forgotSuccess}
                            </Alert>
                        )}

                        <div className="space-y-4">
                            <div>
                                <Input
                                    label="Mobile Number"
                                    type="tel"
                                    value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)}
                                    onBlur={handleMobileBlur}
                                    icon={Phone}
                                    placeholder="e.g. +971501234567"
                                    required
                                    rightElement={
                                        mobileStatusLoading && (
                                            <Loader2 className="w-4 h-4 text-cyan-500 animate-spin" />
                                        )
                                    }
                                    error={mobileError}
                                />
                            </div>

                            <div>
                                <Input
                                    label={hasPin ? '4-Digit PIN' : 'Choose a 4-Digit PIN'}
                                    type="password"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={4}
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
                                    icon={Lock}
                                    placeholder="••••"
                                    className="tracking-[0.5em] font-bold"
                                    required
                                />
                                {!hasPin && (
                                    <div className="p-3.5 bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-100 dark:border-cyan-900/40 rounded-2xl text-xs text-cyan-800 dark:text-cyan-300 font-semibold mt-3 leading-relaxed animate-in fade-in slide-in-from-top-1">
                                        <strong>First-time Sign In:</strong> Welcome {customerName ? <strong>{customerName}</strong> : ''}! Please choose a secure 4-digit PIN above. This will be your login PIN for all future visits.
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between py-1">
                                <Checkbox
                                    label="Remember Me"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                
                                <button 
                                    type="button" 
                                    onClick={() => { setView('forgot-pin'); setError(null); }}
                                    className="text-xs font-bold text-cyan-600 dark:text-sky-400 hover:underline rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Forgot PIN?
                                </button>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-14 text-base font-bold shadow-lg shadow-cyan-600/10 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        Sign In
                                        <ChevronRight className="w-5 h-5" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                )}

                {/* --- FORGOT PIN VIEW --- */}
                {view === 'forgot-pin' && (
                    <form onSubmit={handleRequestPin} className="p-8 space-y-6">
                        {error && (
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        )}

                        {forgotSuccess && (
                            <Alert variant="success">
                                {forgotSuccess}
                            </Alert>
                        )}

                        <div className="space-y-4">
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                                Enter the mobile number associated with your B2B account. We will send a new temporary PIN directly to your number via WhatsApp.
                            </p>

                            <div>
                                <Input
                                    label="Mobile Number"
                                    type="tel"
                                    value={forgotMobile}
                                    onChange={(e) => setForgotMobile(e.target.value)}
                                    icon={Phone}
                                    placeholder="e.g. +971501234567"
                                    required
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={forgotLoading}
                                className="w-full h-14 text-sm font-bold shadow-lg shadow-cyan-600/10 flex items-center justify-center gap-2"
                            >
                                {forgotLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Sending PIN...
                                    </>
                                ) : (
                                    <>
                                        Send Temporary PIN via WhatsApp
                                        <MessageSquare className="w-4 h-4" />
                                    </>
                                )}
                            </Button>

                            <button 
                                type="button" 
                                onClick={() => setView('login')}
                                className="w-full text-center text-xs font-bold text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors pt-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Back to Sign In
                            </button>
                        </div>
                    </form>
                )}

                {/* Footer Info */}
                <div className="bg-slate-50 dark:bg-slate-800/40 p-6 text-center border-t border-slate-100 dark:border-slate-800/50">
                    <p className="text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                        Securely powered by <span className="text-slate-600 dark:text-slate-300 font-black">Yaseero</span>
                    </p>
                </div>
            </Card>
        </div>
    );
};

export default CustomerLoginPage;
